import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { ErrorBoundary, Spinner } from "@/components/States";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordPages";
import AcceptInvite from "@/pages/AcceptInvite";
import Dashboard from "@/pages/Dashboard";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Team from "@/pages/Team";
import PriceBook from "@/pages/PriceBook";
import Workspace from "@/pages/Workspace";
import Billing from "@/pages/Billing";
import Platform from "@/pages/Platform";
import { BillingCancel, BillingSuccess } from "@/pages/BillingResult";
import PublicPresentation from "@/pages/PublicPresentation";
import Lookbook from "@/pages/Lookbook";
import MasterSchedule from "@/pages/MasterSchedule";
import Timesheets from "@/pages/Timesheets";
import TradePass from "@/pages/TradePass";
import HandoverPublic from "@/pages/HandoverPublic";
import { Privacy, Terms } from "@/pages/Legal";
import Pitch from "@/pages/Pitch";
import PreferenceSheet from "@/pages/PreferenceSheet";
import SecurityPolicy from "@/pages/SecurityPolicy";
import MfaSetupGate from "@/pages/MfaSetupGate";
import BetaTermsGate from "@/pages/BetaTermsGate";
import Learn from "@/pages/Learn";
import LearnPrint from "@/pages/LearnPrint";

const Loader = () => <div className="grid h-screen place-items-center" data-testid="auth-loading"><Spinner /></div>;

function Protected({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mfa_setup_required) return <MfaSetupGate />;
  if (user.beta_terms_required) return <BetaTermsGate />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function OwnerOnly({ children }) {
  const { user } = useAuth();
  if (!user?.platform_owner) return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, next } = useAuth();
  if (user) return <Navigate to={next || "/"} replace />;
  return children;
}

function Home() {
  const { user, brand } = useAuth();
  if (user === null) return <Loader />;
  if (user) return <Protected><AppShell /></Protected>;
  return brand.platform && !brand.private_beta ? <Landing /> : <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            <Route path="/share/:token" element={<PublicPresentation />} />
            <Route path="/lookbook/:slug" element={<Lookbook />} />
            <Route path="/pass/:token" element={<TradePass />} />
            <Route path="/handover/:token" element={<HandoverPublic />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/projects/:id/preferences/print" element={<Protected roles={["admin", "crew", "client"]}><PreferenceSheet /></Protected>} />
            <Route path="/learn/guide/print" element={<Protected roles={["admin", "crew", "client"]}><LearnPrint doc="guide" /></Protected>} />
            <Route path="/learn/qa/print" element={<Protected roles={["admin", "crew", "client"]}><LearnPrint doc="qa" /></Protected>} />
            <Route path="/workspace/policies/:slug" element={<Protected roles={["admin"]}><SecurityPolicy /></Protected>} />
            <Route path="/billing/success" element={<Protected roles={["admin"]}><BillingSuccess /></Protected>} />
            <Route path="/billing/cancel" element={<Protected roles={["admin"]}><BillingCancel /></Protected>} />
            <Route element={<Home />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/projects" element={<Protected roles={["admin", "crew"]}><Projects /></Protected>} />
              <Route path="/time" element={<Protected roles={["admin", "crew"]}><Timesheets /></Protected>} />
              <Route path="/schedule" element={<Protected roles={["admin", "crew"]}><MasterSchedule /></Protected>} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/learn" element={<Protected roles={["admin", "crew", "client"]}><Learn /></Protected>} />
              <Route path="/price-book" element={<Protected roles={["admin"]}><PriceBook /></Protected>} />
              <Route path="/team" element={<Protected roles={["admin"]}><Team /></Protected>} />
              <Route path="/workspace" element={<Protected roles={["admin"]}><Workspace /></Protected>} />
              <Route path="/billing" element={<Protected roles={["admin"]}><Billing /></Protected>} />
              <Route path="/platform" element={<OwnerOnly><Platform /></OwnerOnly>} />
              <Route path="/platform/pitch" element={<OwnerOnly><Pitch /></OwnerOnly>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" richColors />
      </AuthProvider>
    </ErrorBoundary>
  );
}
